import React, { createContext, useContext, FC } from 'react';
import useDatesStringByLocale from 'clientState/hooks/useDatesStringByLocale';
import { PillStatus } from '../FiltersPill/FiltersPill';
import { useSemWeekDays } from 'clientState/providers/ParsedSearchResponseProvider';
import { FilterPillSelectorOption } from '../FilterPillSelector/FilterPillSelector';
import { usePerformedSearchRequest } from 'clientState/providers/SearchRequestProvider';

type SemWeekDayFilterContextType = {
  departureDisabled: number[];
  arrivalDisabled: number[];
  weekDays: FilterPillSelectorOption[];
  isRoundTrip: boolean;
  weekDaysSelected: { departureSelected: number[]; arrivalSelected: number[] };
  isSemWeekDaysFilterApplied: boolean;
  isSemWeekDepartureDaysFilterApplied: boolean;
  isSemWeekArrivalDaysFilterApplied: boolean;
  resetSemWeekDaysFilter: () => void;
  applySemWeekDaysDepartureFilter: (days: number[]) => void;
  applySemWeekDaysArrivalFilter: (days: number[]) => void;
};

export const SemWeekDayFilterContext =
  createContext<SemWeekDayFilterContextType>({} as SemWeekDayFilterContextType);

export const useSemWeekDayFilter = (): SemWeekDayFilterContextType =>
  useContext(SemWeekDayFilterContext);

const SemWeekDayFilterProvider: FC = ({ children }) => {
  const { getAbbreviatedDay } = useDatesStringByLocale();
  const { tripType } = usePerformedSearchRequest() || {};
  const isRoundTrip = tripType === 'ROUND_TRIP';
  const data = useSemWeekDays();

  const [departureSelected, setDepartureSelected] = React.useState<number[]>(
    []
  );
  const [arrivalSelected, setArrivalSelected] = React.useState<number[]>([]);
  const [isSemWeekDaysFilterApplied, setSemWeekDaysFilterApplied] =
    React.useState<boolean>(false);
  const [
    isSemWeekDepartureDaysFilterApplied,
    setSemWeekDepartureDaysFilterApplied,
  ] = React.useState<boolean>(false);
  const [
    isSemWeekArrivalDaysFilterApplied,
    setSemWeekArrivalDaysFilterApplied,
  ] = React.useState<boolean>(false);

  const applySemWeekDaysDepartureFilter = (days: number[]): void => {
    setDepartureSelected(days);
    setSemWeekDaysFilterApplied(days.length > 0);
    setSemWeekDepartureDaysFilterApplied(days.length > 0);
  };
  const applySemWeekDaysArrivalFilter = (days: number[]): void => {
    setArrivalSelected(days);
    setSemWeekDaysFilterApplied(days.length > 0);
    setSemWeekArrivalDaysFilterApplied(days.length > 0);
  };

  const resetSemWeekDaysFilter = (): void => {
    setDepartureSelected([]);
    setArrivalSelected([]);
    setSemWeekDaysFilterApplied(false);
    setSemWeekDepartureDaysFilterApplied(false);
    setSemWeekArrivalDaysFilterApplied(false);
  };

  if (!data)
    return (
      <SemWeekDayFilterContext.Provider
        value={{
          weekDays: [],
          departureDisabled: [],
          arrivalDisabled: [],
          weekDaysSelected: { departureSelected: [], arrivalSelected: [] },
          isRoundTrip,
          isSemWeekDaysFilterApplied,
          isSemWeekDepartureDaysFilterApplied,
          isSemWeekArrivalDaysFilterApplied,
          resetSemWeekDaysFilter,
          applySemWeekDaysDepartureFilter,
          applySemWeekDaysArrivalFilter,
        }}
      >
        {children}
      </SemWeekDayFilterContext.Provider>
    );

  const { departureDays, arrivalDays } = data;

  const departureDisabled = [1, 2, 3, 4, 5, 6, 0].filter(
    (day) => !departureDays.has(day)
  );

  const arrivalDisabled = [1, 2, 3, 4, 5, 6, 0].filter(
    (day) => !arrivalDays.has(day)
  );

  const weekDays: FilterPillSelectorOption[] = [1, 2, 3, 4, 5, 6, 0].map(
    (day) => {
      return {
        id: day,
        label: getAbbreviatedDay(day),
        status: PillStatus.default,
      };
    }
  );

  const value = {
    weekDays,
    departureDisabled,
    arrivalDisabled,
    weekDaysSelected: { departureSelected, arrivalSelected },
    isRoundTrip,
    isSemWeekDaysFilterApplied,
    isSemWeekDepartureDaysFilterApplied,
    isSemWeekArrivalDaysFilterApplied,
    resetSemWeekDaysFilter,
    applySemWeekDaysDepartureFilter,
    applySemWeekDaysArrivalFilter,
  };

  return (
    <SemWeekDayFilterContext.Provider value={value}>
      {children}
    </SemWeekDayFilterContext.Provider>
  );
};

export default SemWeekDayFilterProvider;
